import React, { useState } from 'react';
import {
  DataTable,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  TableContainer,
  TableToolbar,
  TableToolbarContent,
  TableToolbarSearch,
  Pagination,
  OverflowMenu,
  OverflowMenuItem,
  Button,
} from '@carbon/react';
import { Edit, TrashCan } from '@carbon/icons-react';

const SearchTable = ({
  title,
  rows: initialRows,
  headers,
  onEditRow,
  actionButtonText,
  onActionClick,
  ...args
}) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Convierte los datos a CSV usando los headers que recibe el componente
  const convertToCSV = (data, headers) => {
    if (!data || data.length === 0) {
      return '';
    }

    // Cabecera del CSV (los textos visibles de la tabla)
    const headerRow = headers.map(h => h.header).join(',') + '\n';

    // Filas de datos
    const rowsCSV = data
      .map(row => {
        return headers
          .map(h => {
            let value = row[h.key];

            // Manejo de null/undefined
            if (value === null || value === undefined) {
              value = '';
            } else if (
              typeof value === 'number' &&
              !Number.isNaN(value) &&
              value.toString().includes('.')
            ) {
              // Reemplazar punto decimal por coma
              value = value.toString().replace('.', ',');
            }

            // Aseguramos texto entre comillas
            return `"${value}"`;
          })
          .join(',');
      })
      .join('\n');

    return headerRow + rowsCSV;
  };

  const downloadCSV = (csvContent, fileName = 'tabla.csv') => {
    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownload = () => {
    const csv = convertToCSV(initialRows, headers);
    const fileName = (title ? title.replace(/\s+/g, '_') : 'tabla') + '.csv';
    downloadCSV(csv, fileName);
  };

  return (
    <DataTable
      rows={initialRows}
      headers={headers}
      {...args}
      isSortable
    >
      {({
        rows,
        headers,
        getHeaderProps,
        getRowProps,
        getTableProps,
        getToolbarProps,
        onInputChange,
        getTableContainerProps,
      }) => (
        <TableContainer title={title} {...getTableContainerProps()}>
          <TableToolbar {...getToolbarProps()} aria-label="barra de herramientas de la tabla">
            <TableToolbarContent>
              <TableToolbarSearch placeholder="Buscar" onChange={onInputChange} />
              <Button kind="secondary" onClick={handleDownload}>
                Descargar
              </Button>
            </TableToolbarContent>
          </TableToolbar>

          <Table {...getTableProps()} aria-label="tabla">
            <TableHead>
              <TableRow>
                {headers.map(header => {
                  const { key, ...rest } = getHeaderProps({ header });
                  return (
                    <TableHeader key={key} {...rest}>
                      {header.header}
                    </TableHeader>
                  );
                })}
                {(onEditRow || onActionClick) && <TableHeader></TableHeader>}
              </TableRow>
            </TableHead>

            <TableBody>
              {rows
                .slice((page - 1) * pageSize, page * pageSize)
                .map(row => {
                  const { key: rowKey, ...rowProps } = getRowProps({ row });
                  return (
                    <TableRow key={rowKey} {...rowProps}>
                      {row.cells.map(cell => (
                        <TableCell key={cell.id}>{cell.value}</TableCell>
                      ))}

                      {(onEditRow || onActionClick) && (
                        <TableCell className="actions-cell">
                          <OverflowMenu
                            flipped
                            iconDescription="Acciones"
                            aria-label="Menú de acciones"
                          >
                            <OverflowMenuItem
                              itemText={actionButtonText || 'Editar'}
                              onClick={() => {
                                if (onActionClick) {
                                  onActionClick(row);
                                } else if (onEditRow) {
                                  onEditRow(row.id);
                                }
                              }}
                              renderIcon={Edit}
                            />
                            <OverflowMenuItem
                              itemText="Eliminar"
                              onClick={() => alert(`Eliminar fila ${row.id}`)}
                              renderIcon={TrashCan}
                              isDelete
                            />
                          </OverflowMenu>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>

          <Pagination
            totalItems={rows.length}
            pageSizes={[10, 15, 20]}
            itemsPerPageText="Elementos por página"
            pageRangeText={(current, total) => `${current}–${total} página(s)`}
            itemRangeText={(min, max) => `${min}–${max} Filas`}
            pageNumberText="Número de página"
            page={page}
            pageSize={pageSize}
            onChange={({ page, pageSize }) => {
              setPage(page);
              setPageSize(pageSize);
            }}
            forwardText="Página siguiente"
            backwardText="Página anterior"
          />
        </TableContainer>
      )}
    </DataTable>
  );
};

export default SearchTable;